import { Suspense, useEffect, useState } from 'react'
import {
  BarChart3,
  Bell,
  CalendarDays,
  Check,
  ClipboardList,
  Construction,
  Container,
  FileText,
  Fuel,
  Bot,
  LayoutGrid,
  Map,
  Menu,
  Search,
  Settings,
  Truck,
  Users,
  Wrench,
  X,
} from 'lucide-react'
import { Notifications } from '../shared/Notifications'
import { readinessFor } from '../domain/readiness'
import type { ComponentType } from 'react'
import type { Role, View } from '../domain/models'
import { appStore, useAppState } from './state'
import { routes } from './routes'
import { loadProject, useConnection, useProject } from './data'
import { Modal } from '../shared/ui'
import LoginPage from '../pages/LoginPage'
import GlobalSearch from '../shared/GlobalSearch'

type NavItem = { view: View; label: string; icon: ComponentType<{ className?: string }> }
const nav: NavItem[] = [
  { view: 'yard', label: 'ЖД терминал', icon: Container },
  { view: 'dashboard', label: 'Обзор', icon: BarChart3 },
  { view: 'fleet', label: 'Техника', icon: Truck },
  { view: 'workorders', label: 'Работы механика', icon: Wrench },
  { view: 'maintenance', label: 'Регламент ТО', icon: CalendarDays },
  { view: 'resources', label: 'Материалы', icon: Construction },
  { view: 'documents', label: 'Документы', icon: FileText },
  { view: 'costs', label: 'Затраты', icon: BarChart3 },
  { view: 'notifications', label: 'Уведомления', icon: Bell },
  { view: 'assistant', label: 'Помощник', icon: Bot },
  { view: 'settings', label: 'Настройки', icon: Settings },
]
const mechanicNav: NavItem[] = [
  { view: 'yard', label: 'ЖД площадка', icon: Container },
  { view: 'mechanic', label: 'Работа', icon: Wrench },
  { view: 'mechanicFleet', label: 'Техника', icon: Truck },
  { view: 'mechanicMaterials', label: 'Материалы', icon: Construction },
  { view: 'mechanicEvents', label: 'События', icon: Bell },
  { view: 'assistant', label: 'Помощник', icon: Bot },
]
const operatorNav: NavItem[] = [
  { view: 'operator', label: 'Моя техника', icon: Truck },
  { view: 'operatorMessages', label: 'Сообщения', icon: Bell },
  { view: 'profile', label: 'Профиль', icon: Users },
]
const managerMobileNav: NavItem[] = [
  { view: 'yard', label: 'ЖД терминал', icon: Container },
  { view: 'dashboard', label: 'Обзор', icon: BarChart3 },
  { view: 'fleet', label: 'Техника', icon: Truck },
  { view: 'workorders', label: 'Работы', icon: Wrench },
]
const support: NavItem[] = [
  { view: 'fuel', label: 'Топливо', icon: Fuel },
  { view: 'operators', label: 'Операторы', icon: Users },
  { view: 'imports', label: 'Импорт Excel', icon: FileText },
]
const adjacent: NavItem[] = [
  { view: 'requests', label: 'Заявки на технику', icon: ClipboardList },
  { view: 'schedule', label: 'Расписание', icon: CalendarDays },
  { view: 'map', label: 'Карта и объекты', icon: Map },
  { view: 'clients', label: 'Клиенты', icon: Construction },
  { view: 'procurement', label: 'Закупки', icon: ClipboardList },
]
const homeFor = (role: string): View =>
  role === 'owner'
    ? 'yard'
    : role === 'mechanic'
      ? 'mechanic'
      : role === 'operator'
        ? 'operator'
        : role === 'client' || role === 'customer'
          ? 'client'
          : 'dashboard'
const allowedFor = (role: string): View[] => {
  const pages: View[] =
    role === 'owner'
      ? (Object.keys(routes) as View[])
      : role === 'manager'
        ? [
            'yard',
            'dashboard',
            'fleet',
            'workorders',
            'resources',
            'documents',
            'costs',
            'fuel',
            'maintenance',
            'notifications',
            'assistant',
          ]
        : role === 'mechanic'
          ? [
              'yard',
              'mechanic',
              'mechanicFleet',
              'mechanicMaterials',
              'mechanicEvents',
              'assistant',
              'fleet',
              'resources',
              'maintenance',
              'workorders',
            ]
          : role === 'operator'
            ? ['operator', 'operatorMessages']
            : role === 'client'
              ? ['client']
              : role === 'dispatcher'
                ? [...nav, ...support, ...adjacent].map((item) => item.view)
                : []
  return [...pages, 'profile', ...(role === 'client' ? [] : ['search' as View])]
}
export default function AppShell() {
  const { role, view, toast } = useAppState()
  const data = useProject()
  const connection = useConnection()
  const [menuOpen, setMenuOpen] = useState(false)
  const [product, setProduct] = useState<'mechanization' | 'dispatch'>('mechanization')
  useEffect(() => {
    setProduct(adjacent.some((item) => item.view === view) ? 'dispatch' : 'mechanization')
  }, [view])
  const [notifications, setNotifications] = useState(false)
  useEffect(() => {
    void loadProject(role, true)
  }, [role])
  useEffect(() => {
    const refresh = () => void loadProject(role)
    window.addEventListener('online', refresh)
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh()
    }, 15000)
    return () => {
      window.removeEventListener('online', refresh)
      window.clearInterval(timer)
    }
  }, [role])
  const serverRole =
    connection.session && connection.session.appMode !== 'demo' ? connection.session.role : undefined
  const effectiveRole = serverRole === 'customer' ? 'client' : (serverRole ?? role)
  const allowed = allowedFor(effectiveRole)
  const safeView: View = allowed.includes(view) ? view : homeFor(effectiveRole)
  const deniedView = !allowed.includes(view)
  useEffect(() => {
    if (connection.session?.appMode !== 'demo' && connection.session && deniedView) {
      appStore.navigate(safeView, undefined, true)
    }
  }, [connection.session, deniedView, safeView])
  const Page = routes[safeView].component
  const navItems =
    effectiveRole === 'client'
      ? [{ view: 'client' as View, label: 'Мои заявки', icon: ClipboardList }]
      : effectiveRole === 'mechanic'
        ? mechanicNav
        : effectiveRole === 'operator'
          ? operatorNav
          : effectiveRole === 'manager'
            ? nav.filter((n) =>
                [
                  'yard',
                  'dashboard',
                  'fleet',
                  'workorders',
                  'maintenance',
                  'resources',
                  'documents',
                  'costs',
                  'notifications',
                  'assistant',
                ].includes(n.view),
              )
            : product === 'dispatch'
              ? adjacent
              : nav
  const ready = data.equipment.filter((e) => readinessFor(data, e).status === 'ready').length
  const mobileNav =
    effectiveRole === 'mechanic'
      ? mechanicNav
      : effectiveRole === 'operator'
        ? operatorNav
        : effectiveRole === 'client'
          ? []
          : managerMobileNav
  const unread = data.notifications.filter(
    (n) => !n.readBy.includes(connection.session?.user.id ?? ''),
  ).length
  if (connection.unauthorized) {
    return (
      <div className="login-shell">
        <LoginPage
          sessionError={connection.error === 'Требуется вход в систему' ? undefined : connection.error}
          onSignedIn={async (signedRole) => {
            const result = await loadProject(role, true)
            if (!result.ok) throw new Error(result.error)
            const normalized = signedRole === 'customer' ? 'client' : signedRole
            const target =
              window.location.pathname === '/login' || !allowedFor(normalized).includes(view)
                ? homeFor(normalized)
                : view
            appStore.navigate(target, undefined, true)
          }}
        />
      </div>
    )
  }
  return (
    <div className={'app-shell ' + (['client', 'operator'].includes(effectiveRole) ? 'portal-shell' : '')}>
      <aside className={'sidebar ' + (menuOpen ? 'open' : '')}>
        <button
          className="icon-button sidebar-close"
          aria-label="Закрыть меню"
          onClick={() => setMenuOpen(false)}
        >
          <X className="icon" />
        </button>
        <div className="brand">
          <div className="brand-mark">
            <Truck className="icon" />
          </div>
          <div className="brand-copy">
            <strong>МАГИСТРАЛЬ</strong>
            <span>Техническая готовность парка</span>
          </div>
        </div>
        <nav className="main-nav" aria-label="Основная навигация">
          {(effectiveRole === 'dispatcher' || effectiveRole === 'owner') && (
            <button
              className="nav-button product-switch"
              onClick={() => {
                const next = product === 'mechanization' ? 'dispatch' : 'mechanization'
                setProduct(next)
                appStore.navigate(next === 'dispatch' ? 'requests' : 'dashboard')
                setMenuOpen(false)
              }}
            >
              <LayoutGrid className="icon" />{' '}
              {product === 'mechanization' ? 'Перейти в СД и учёт' : 'Вернуться в Механизацию'}
            </button>
          )}
          <span className="nav-caption">Рабочее пространство</span>
          {navItems.map(({ view: target, label, icon: Icon }) => (
            <button
              key={target}
              className={'nav-button ' + (safeView === target ? 'active' : '')}
              onClick={() => {
                appStore.navigate(target)
                setMenuOpen(false)
              }}
            >
              <Icon className="icon" />
              {label}
            </button>
          ))}
          {(effectiveRole === 'dispatcher' || effectiveRole === 'owner') && product === 'mechanization' && (
            <>
              <span className="nav-caption">Справочники и данные</span>
              {support.map(({ view: target, label, icon: Icon }) => (
                <button
                  key={target}
                  className={'nav-button ' + (safeView === target ? 'active' : '')}
                  onClick={() => {
                    appStore.navigate(target)
                    setMenuOpen(false)
                  }}
                >
                  <Icon className="icon" />
                  {label}
                </button>
              ))}
            </>
          )}
        </nav>
        <div className="sidebar-summary">
          {connection.session && (
            <button
              className="button ghost"
              onClick={() => {
                setMenuOpen(false)
                appStore.navigate('profile')
              }}
            >
              Мой профиль
            </button>
          )}
          <span>Состояние данных</span>
          <strong>
            {connection.loading ? 'Обновление…' : connection.error ? 'Нет связи' : 'Данные сохранены'}
          </strong>
          {effectiveRole !== 'client' && (
            <p>
              Готовы к работе: {ready} из {data.equipment.length}
            </p>
          )}
          <button className="button ghost" onClick={() => void loadProject(role)}>
            Обновить
          </button>
        </div>
      </aside>
      {menuOpen && (
        <button className="menu-backdrop" aria-label="Закрыть меню" onClick={() => setMenuOpen(false)} />
      )}
      <main className="app-main">
        <header className="topbar">
          <div className="topbar-actions">
            <button
              className="icon-button menu-button"
              aria-label="Открыть меню"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="icon" /> : <Menu className="icon" />}
            </button>
            <div className="context-pill">
              <span>Текущий раздел</span>
              <strong>{routes[safeView].title}</strong>
            </div>
          </div>
          <div className="topbar-actions">
            {effectiveRole !== 'client' && effectiveRole !== 'operator' && (
              <GlobalSearch role={effectiveRole} />
            )}
            {effectiveRole !== 'client' && effectiveRole !== 'operator' && (
              <button
                className="icon-button mobile-search-button"
                aria-label="Открыть поиск"
                onClick={() => appStore.navigate('search')}
              >
                <Search className="icon" />
              </button>
            )}
            {
              <button className="icon-button" aria-label="Уведомления" onClick={() => setNotifications(true)}>
                <Bell className="icon" />
                {unread > 0 && <sup>{unread}</sup>}
              </button>
            }
            {connection.session?.appMode === 'demo' ? (
              <div className="role-switcher">
                <Settings className="icon" />
                <span>Локальный пилот</span>
                <select
                  value={role}
                  onChange={(event) => appStore.setRole(event.target.value as Role)}
                  aria-label="Выбрать роль"
                >
                  <option value="owner">Владелец предприятия</option>
                  <option value="manager">Руководитель</option>
                  <option value="dispatcher">Ответственный за механизацию</option>
                  <option value="mechanic">Механик</option>
                  <option value="operator">Оператор</option>
                </select>
              </div>
            ) : (
              connection.session && (
                <button className="button ghost profile-button" onClick={() => appStore.navigate('profile')}>
                  <Users className="icon" /> <span>Мой профиль</span>
                </button>
              )
            )}
          </div>
        </header>
        {data.readiness?.demo && (
          <div className="readiness-demo-note">
            Демонстрационный контур · состояния и затраты учебные. Исходная рабочая база не изменена.
          </div>
        )}
        {connection.loading && data.revision === 0 ? (
          <div className="loading-screen">Загружаем данные с сервера…</div>
        ) : connection.error && data.revision === 0 ? (
          <div className="content">
            <h1>Сервер недоступен</h1>
            <p role="alert">{connection.error}</p>
            <button className="button primary" onClick={() => void loadProject(role)}>
              Повторить подключение
            </button>
          </div>
        ) : (
          <>
            {connection.error && (
              <div className="alert" role="alert">
                {connection.error}
              </div>
            )}
            <Suspense fallback={<div className="loading-screen">Загрузка экрана…</div>}>
              <Page />
            </Suspense>
          </>
        )}
      </main>
      {mobileNav.length > 0 && (
        <nav className="mobile-role-nav" aria-label="Нижняя навигация">
          {mobileNav.map(({ view: target, label, icon: Icon }) => (
            <button
              key={target}
              type="button"
              className={safeView === target ? 'active' : ''}
              aria-current={safeView === target ? 'page' : undefined}
              onClick={() => {
                appStore.navigate(target)
                setMenuOpen(false)
              }}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      )}
      {toast && (
        <div className="toast" role="status">
          <Check className="icon" />
          <div>
            <strong>{toast.title}</strong>
            <span>{toast.message}</span>
          </div>
        </div>
      )}
      {notifications && (
        <Modal title="Уведомления" onClose={() => setNotifications(false)}>
          <Notifications />
        </Modal>
      )}
    </div>
  )
}
